"use client";

import React, { useLayoutEffect, useRef, useCallback } from "react";
import Lenis from "lenis";

export const ScrollStackItem = ({ children, itemClassName = "" }) => (
  <div
    className={`scroll-stack-card ${itemClassName}`.trim()}
    style={{
      position: "relative",
      width: "100%",
      margin: 0, // spacing is controlled programmatically for accurate stacking
      boxSizing: "border-box",
      transformStyle: "preserve-3d",
      willChange: "transform, filter",
      backfaceVisibility: "hidden",
      WebkitBackfaceVisibility: "hidden",
    }}
  >
    {children}
  </div>
);

const ScrollStack = ({
  children,
  className = "",
  itemDistance = 100,
  itemScale = 0.03,
  itemStackDistance = 30,
  stackPosition = "20%",
  scaleEndPosition = "10%",
  baseScale = 0.85,
  rotationAmount = 0,
  blurAmount = 0,
  useWindowScroll = true,
  onStackComplete,
}) => {
  const scrollerRef = useRef(null);
  const stackCompletedRef = useRef(false);
  const animationFrameRef = useRef(null);
  const lenisRef = useRef(null);
  const scrollYRef = useRef(0);
  const cardsRef = useRef([]);
  // Cache each card's natural (untransformed) top offset to avoid feedback from transforms
  const baseOffsetsRef = useRef([]);
  const heightsRef = useRef([]);
  // Store final translateY for cards after they are released so they don't drift later
  const releaseYRef = useRef(new Map());
  // Store frozen Y for cards once the next card becomes top (prevents earlier cards from shifting when a later card pins)
  const freezeYRef = useRef(new Map());
  const lastTransformsRef = useRef(new Map());
  const isUpdatingRef = useRef(false);

  const calculateProgress = useCallback((scrollTop, start, end) => {
    if (scrollTop < start) return 0;
    if (scrollTop > end) return 1;
    return (scrollTop - start) / (end - start);
  }, []);

  const parsePercentage = useCallback((value, containerHeight) => {
    if (typeof value === "string" && value.includes("%")) {
      return (parseFloat(value) / 100) * containerHeight;
    }
    return parseFloat(value);
  }, []);

  const getScrollData = useCallback(() => {
    if (useWindowScroll) {
      return {
        scrollTop: scrollYRef.current || window.scrollY,
        containerHeight: window.innerHeight,
        scrollContainer: document.documentElement,
      };
    } else {
      const scroller = scrollerRef.current;
      return {
        scrollTop: scroller.scrollTop,
        containerHeight: scroller.clientHeight,
        scrollContainer: scroller,
      };
    }
  }, [useWindowScroll]);

  const getElementOffset = useCallback(
    (element) => {
      // Return document-based offset for window scroll; offsetTop for container scroll
      if (useWindowScroll) {
        const rect = element.getBoundingClientRect();
        return rect.top + window.scrollY;
      } else {
        return element.offsetTop;
      }
    },
    [useWindowScroll]
  );

  const updateCardTransforms = useCallback(() => {
    if (!cardsRef.current.length || isUpdatingRef.current) return;
    isUpdatingRef.current = true;

    const { scrollTop, containerHeight } = getScrollData();
    const stackPositionPx = parsePercentage(stackPosition, containerHeight);
    const scaleEndPositionPx = parsePercentage(scaleEndPosition, containerHeight);

    const endElement = useWindowScroll
      ? document.querySelector(".scroll-stack-end")
      : scrollerRef.current?.querySelector(".scroll-stack-end");
    const endElementTop = endElement ? getElementOffset(endElement) : 0;

    // Compute the current absolute top of the inner container; using offsetTop + innerTop avoids transforms
    const innerEl = useWindowScroll
      ? document.querySelector('.scroll-stack-inner')
      : scrollerRef.current?.querySelector('.scroll-stack-inner');
    const innerTopNow = innerEl ? getElementOffset(innerEl) : 0;

    // If any card height changed (image load etc.), recompute base offsets and heights, then rerun next frame
    let heightsChanged = false;
    const currentHeights = cardsRef.current.map((el) => el.offsetHeight || 0);
    if (heightsRef.current.length !== currentHeights.length) {
      heightsChanged = true;
    } else {
      for (let i = 0; i < currentHeights.length; i++) {
        if (Math.abs((heightsRef.current[i] || 0) - currentHeights[i]) > 1) {
          heightsChanged = true;
          break;
        }
      }
    }
    if (heightsChanged) {
      // Recalculate offsets using offsetTop relative to the inner container (immune to transforms)
      const inner = useWindowScroll
        ? document.querySelector('.scroll-stack-inner')
        : scrollerRef.current?.querySelector('.scroll-stack-inner');
      const innerTop = inner ? getElementOffset(inner) : 0;
      baseOffsetsRef.current = cardsRef.current.map((el) => innerTop + el.offsetTop);
      heightsRef.current = currentHeights;
      isUpdatingRef.current = false;
      return;
    }

    // Determine which card is currently the "top" card (the latest one that has reached its pin start)
    let topCardIndex = 0;
    for (let j = 0; j < cardsRef.current.length; j++) {
      const el = cardsRef.current[j];
      const elTop = baseOffsetsRef.current[j] ?? 0;
      const elHeight = el ? el.offsetHeight || 0 : 0;
      const elPinTarget = stackPositionPx - elHeight / 2;
      const elCenterReach = elTop - elPinTarget;
      if (scrollTop >= elCenterReach) topCardIndex = j;
    }

    cardsRef.current.forEach((card, i) => {
      const cardTop = baseOffsetsRef.current[i] ?? (innerTopNow + (card?.offsetTop || 0));
      const cardHeight = card.offsetHeight || 0;
      // Pin target so the CARD CENTER sits at the desired stack position (e.g., 50% viewport)
      const pinTarget = stackPositionPx - cardHeight / 2;

      const triggerStart = cardTop - pinTarget - itemStackDistance * i;
      const triggerEnd = cardTop - scaleEndPositionPx;
      // Pin when the card's center reaches the stack position
      const pinStart = cardTop - pinTarget - itemStackDistance * i;
      // Keep ALL non-last cards pinned until the end marker so all three remain visible together
      let pinEnd = endElementTop - containerHeight / 2;
      if (pinEnd <= pinStart) pinEnd = pinStart + 1;

      // Freeze all animated properties after release by clamping progress to pinEnd
      const progressScroll = Math.min(scrollTop, pinEnd);
      const scaleProgress = calculateProgress(progressScroll, triggerStart, triggerEnd);
      const targetScale = baseScale + i * itemScale;
      const scale = 1 - scaleProgress * (1 - targetScale);
      const rotation = rotationAmount ? i * rotationAmount * scaleProgress : 0;

      let blur = 0;
      if (blurAmount) {
        let topCardIndex = 0;
        for (let j = 0; j < cardsRef.current.length; j++) {
          const jCardTop = getElementOffset(cardsRef.current[j]);
          const jTriggerStart = jCardTop - stackPositionPx - itemStackDistance * j;
          if (scrollTop >= jTriggerStart) topCardIndex = j;
        }
        if (i < topCardIndex) blur = Math.max(0, (topCardIndex - i) * blurAmount);
      }

      // Translate: before pinStart -> normal flow; during pin -> center with a clamped relative stack offset; after release -> freeze at released Y
      let translateY = 0;
      if (scrollTop < pinStart) {
        releaseYRef.current.delete(i);
        freezeYRef.current.delete(i);
      } else if (scrollTop >= pinStart && scrollTop <= pinEnd) {
        // While pinned, compute depth-based offset live so all three edges are visible (first: -2*peek, second: -1*peek, top: 0)
        releaseYRef.current.delete(i);
        const maxPeek = Math.min(itemStackDistance, 28);
        const depthBehindTop = Math.max(0, topCardIndex - i);
        const stackOffsetPinned = -maxPeek * depthBehindTop; // show 3 edges: top(0), second(-peek), first(-2*peek)
        const pinnedY = scrollTop - cardTop + pinTarget + stackOffsetPinned;
        translateY = pinnedY;
      } else {
        let stored = releaseYRef.current.get(i);
        if (stored == null) {
          const maxPeek = Math.min(itemStackDistance, 28);
          const depthBehindTop = Math.max(0, topCardIndex - i);
          const stackOffsetRelease = -maxPeek * depthBehindTop;
          stored = pinEnd - cardTop + pinTarget + stackOffsetRelease;
          releaseYRef.current.set(i, stored);
        }
        translateY = freezeYRef.current.get(i) ?? stored;
      }

      // Keep full opacity for all stacked cards to avoid perceived movement during stacking
      const visualOpacity = 1;

      const newTransform = {
        translateY: Math.round(translateY * 100) / 100,
        scale: Math.round(scale * 1000) / 1000,
        rotation: Math.round(rotation * 100) / 100,
        blur: Math.round(blur * 100) / 100,
      };

      const last = lastTransformsRef.current.get(i);
      const alpha = 0.2; // smoothing factor (0..1). Higher = snappier, lower = smoother
      const smoothed = last
        ? {
            translateY: last.translateY + (newTransform.translateY - last.translateY) * alpha,
            scale: last.scale + (newTransform.scale - last.scale) * alpha,
            rotation: last.rotation + (newTransform.rotation - last.rotation) * alpha,
            blur: last.blur + (newTransform.blur - last.blur) * alpha,
          }
        : newTransform;

      // Snap to target when very close to avoid micro-jitter
      if (Math.abs(smoothed.translateY - newTransform.translateY) < 0.05) smoothed.translateY = newTransform.translateY;
      if (Math.abs(smoothed.scale - newTransform.scale) < 0.0005) smoothed.scale = newTransform.scale;
      if (Math.abs(smoothed.rotation - newTransform.rotation) < 0.05) smoothed.rotation = newTransform.rotation;
      if (Math.abs(smoothed.blur - newTransform.blur) < 0.05) smoothed.blur = newTransform.blur;

      const transform = `translate3d(0, ${smoothed.translateY}px, 0) scale(${smoothed.scale}) rotate(${smoothed.rotation}deg)`;
      const filter = smoothed.blur > 0 ? `blur(${smoothed.blur}px)` : "";
      card.style.transform = transform;
      card.style.filter = filter;
      card.style.opacity = String(visualOpacity);
      // Ensure stacking order: later items appear above earlier ones
      card.style.zIndex = String(100 + i);
      lastTransformsRef.current.set(i, smoothed);

      if (i === cardsRef.current.length - 1) {
        const inView = scrollTop >= pinStart && scrollTop <= pinEnd;
        if (inView && !stackCompletedRef.current) {
          stackCompletedRef.current = true;
          onStackComplete?.();
        } else if (!inView && stackCompletedRef.current) {
          stackCompletedRef.current = false;
        }
      }
    });

    isUpdatingRef.current = false;
  }, [
    itemScale,
    itemStackDistance,
    stackPosition,
    scaleEndPosition,
    baseScale,
    rotationAmount,
    blurAmount,
    useWindowScroll,
    onStackComplete,
    calculateProgress,
    parsePercentage,
    getScrollData,
    getElementOffset,
  ]);

  const handleScroll = useCallback(() => updateCardTransforms(), [updateCardTransforms]);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const cards = Array.from(
      useWindowScroll
        ? document.querySelectorAll(".scroll-stack-card")
        : scroller?.querySelectorAll(".scroll-stack-card") || []
    );
    cardsRef.current = cards;

    cards.forEach((card, i) => {
      if (i < cards.length - 1) card.style.marginBottom = `${itemDistance}px`;
      card.style.transformOrigin = "top center";
      card.style.backfaceVisibility = "hidden";
    });

    // Capture natural offsets AFTER margins are applied, BEFORE transforms
    const inner = useWindowScroll
      ? document.querySelector('.scroll-stack-inner')
      : scroller?.querySelector('.scroll-stack-inner');
    const innerTop = inner ? getElementOffset(inner) : 0;
    baseOffsetsRef.current = cards.map((el) => innerTop + el.offsetTop);

    // Lenis setup
    const lenis = new Lenis({
      duration: 1.25,
      easing: (t) => 1 - Math.pow(1 - t, 4), // quartOut for a smoother ease-out feel
      smoothWheel: true,
      smoothTouch: true,
    });
    lenisRef.current = lenis;
    lenis.on("scroll", ({ scroll }) => {
      scrollYRef.current = scroll;
      updateCardTransforms();
    });

    const raf = (time) => {
      lenis.raf(time);
      animationFrameRef.current = requestAnimationFrame(raf);
    };
    animationFrameRef.current = requestAnimationFrame(raf);

    // Fallback: also listen to native window scroll and resize to keep transforms in sync
    const recomputeBaseOffsets = () => {
      const innerEl = useWindowScroll
        ? document.querySelector('.scroll-stack-inner')
        : scrollerRef.current?.querySelector('.scroll-stack-inner');
      const innerTopNow = innerEl ? getElementOffset(innerEl) : 0;
      const els = cardsRef.current;
      baseOffsetsRef.current = els.map((el) => innerTopNow + el.offsetTop);
      updateCardTransforms();
    };
    if (useWindowScroll) {
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("resize", recomputeBaseOffsets);
      window.addEventListener("load", recomputeBaseOffsets);
    }

    updateCardTransforms();

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      lenisRef.current?.destroy?.();
      if (useWindowScroll) {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", recomputeBaseOffsets);
        window.removeEventListener("load", recomputeBaseOffsets);
      }
      cardsRef.current = [];
      lastTransformsRef.current.clear();
      baseOffsetsRef.current = [];
      freezeYRef.current.clear();
      releaseYRef.current.clear();
      isUpdatingRef.current = false;
    };
  }, [handleScroll, itemDistance, useWindowScroll, updateCardTransforms]);

  const containerStyle = useWindowScroll
    ? { overscrollBehavior: "contain" }
    : { overscrollBehavior: "contain", overflowY: "auto" };

  const containerClass = useWindowScroll ? `relative w-full ${className}`.trim() : `relative w-full ${className}`.trim();

  return (
    <div className={containerClass} ref={scrollerRef} style={containerStyle}>
      <div className="scroll-stack-inner" style={{ paddingTop: "50vh", paddingBottom: "40vh", minHeight: "100vh" }}>
        {children}
        <div className="scroll-stack-end" style={{ width: "100%", height: 1 }} />
      </div>
    </div>
  );
};

export default ScrollStack;
